import React, { useContext, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { contextApi } from "../context/contextApi";
import { api, handleToast } from "../services/config";

const PRIORITY_OPTIONS = [
  { value: "1", label: "کم" },
  { value: "2", label: "معمولی" },
  { value: "3", label: "بالا" },
  { value: "4", label: "فوری" },
];

const fieldClass =
  "w-full rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-[#0D1117] text-slate-800 dark:text-slate-100 px-3 py-2.5 text-sm outline-none transition-colors focus:border-sky-400 dark:focus:border-sky-500";

const errorFieldClass =
  "border-red-400 bg-red-50 dark:bg-red-950/40 focus:border-red-400";

export const CreateTicket = () => {
  const context = useContext(contextApi);
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
  } = useForm({ mode: "onChange" });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get("categories?f[type]=ticket", {
          headers: {
            Accept: "application/json",
            authorization: `Bearer ${localStorage.getItem("login")}`,
          },
        });
        setCategories(response.data?.data || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
        handleToast("error", "خطا در دریافت دسته‌بندی‌ها");
      }
    };

    fetchCategories();
  }, []);

  const handleClick = (buttonName) => {
    context.recordClick(buttonName);
  };

  const syncFiles = (files) => {
    setSelectedFiles(files);
    const dataTransfer = new DataTransfer();
    files.forEach((file) => dataTransfer.items.add(file));
    setValue("attachments", dataTransfer.files, { shouldValidate: false });
  };

  const openFilePicker = () => {
    handleClick("افزودن پیوست در ایجاد تیکت کلیک شد");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event) => {
    const incomingFiles = Array.from(event.target.files || []);
    if (!incomingFiles.length) return;

    const mergedFiles = [...selectedFiles];
    incomingFiles.forEach((file) => {
      const alreadySelected = mergedFiles.some(
        (selected) =>
          selected.name === file.name &&
          selected.size === file.size &&
          selected.lastModified === file.lastModified
      );
      if (!alreadySelected) {
        mergedFiles.push(file);
      }
    });

    syncFiles(mergedFiles);
    event.target.value = "";
  };

  const removeFile = (index) => {
    syncFiles(selectedFiles.filter((_, fileIndex) => fileIndex !== index));
  };

  const formatFileSize = (size) => {
    if (!size) return "";
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const onSubmit = async (data) => {
    handleClick("ارسال در ایجاد تیکت کلیک شد");
    setSubmitting(true);

    const formData = new FormData();
    formData.append("category_id", data.category_id);
    formData.append("subject", data.subject.trim());
    formData.append("message", data.message.trim());
    formData.append("priority", data.priority);

    selectedFiles.forEach((file, index) => {
      formData.append(`attachments[${index}]`, file);
    });

    try {
      await api.post("tickets", formData, {
        headers: {
          Accept: "application/json",
          authorization: `Bearer ${localStorage.getItem("login")}`,
        },
      });
      handleToast("success", "تیکت با موفقیت ایجاد شد");
      navigate("/support");
    } catch (error) {
      const apiErrors = error.response?.data?.errors;
      if (apiErrors) {
        const firstError = Object.values(apiErrors)[0];
        handleToast(
          "error",
          Array.isArray(firstError) ? firstError[0] : firstError
        );
      } else {
        handleToast(
          "error",
          error.response?.data?.message || "خطا در ایجاد تیکت"
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full min-h-screen bg-white dark:bg-[#0D1117]">
      <ToastContainer />
      <div className="mb-4 p-3 lg:px-6 lg:pt-6">
        <h1
          className="
            relative inline-block text-3xl font-bold mb-4
            text-[#0010c6] dark:text-white
            after:content-[''] after:block after:w-full after:h-[4px]
            after:bg-[#0010c6] after:mt-[10px] dark:after:bg-[#ffffff]
          "
        >
          ایجاد تیکت
        </h1>
      </div>

      <div className="w-[95%] max-w-2xl mx-auto pb-10">
        <div className="flex justify-start mb-4">
          <button
            type="button"
            onClick={() => {
              handleClick("بازگشت از ایجاد تیکت کلیک شد");
              navigate("/support");
            }}
            className="text-sm bg-slate-600 hover:bg-slate-700 px-4 py-2 rounded-xl text-white"
          >
            بازگشت
          </button>
        </div>

        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-[#161B22] p-4 md:p-6 shadow-sm">
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">
            موضوع و پیام را وارد کنید تا پشتیبانی بررسی کند.
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm mb-1.5 text-slate-700 dark:text-slate-200">
                دسته‌بندی
              </label>
              <select
                {...register("category_id", { required: true })}
                className={`${fieldClass} ${
                  errors.category_id ? errorFieldClass : ""
                }`}
              >
                <option value="">انتخاب کنید</option>
                {categories.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
              {errors.category_id && (
                <p className="text-red-500 text-xs mt-1">
                  لطفا دسته‌بندی را انتخاب کنید
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm mb-1.5 text-slate-700 dark:text-slate-200">
                موضوع
              </label>
              <input
                type="text"
                {...register("subject", { required: true })}
                className={`${fieldClass} ${
                  errors.subject ? errorFieldClass : ""
                }`}
                placeholder="مثلاً مشکل در برداشت"
              />
              {errors.subject && (
                <p className="text-red-500 text-xs mt-1">
                  لطفا موضوع را وارد کنید
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm mb-1.5 text-slate-700 dark:text-slate-200">
                اولویت
              </label>
              <select
                {...register("priority", { required: true })}
                className={`${fieldClass} ${
                  errors.priority ? errorFieldClass : ""
                }`}
              >
                <option value="">انتخاب کنید</option>
                {PRIORITY_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.priority && (
                <p className="text-red-500 text-xs mt-1">
                  لطفا اولویت را انتخاب کنید
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm mb-1.5 text-slate-700 dark:text-slate-200">
                پیام
              </label>
              <textarea
                {...register("message", { required: true })}
                rows={5}
                className={`${fieldClass} resize-none leading-6 ${
                  errors.message ? errorFieldClass : ""
                }`}
                placeholder="توضیح کامل مشکل یا درخواست..."
              />
              {errors.message && (
                <p className="text-red-500 text-xs mt-1">
                  لطفا پیام را وارد کنید
                </p>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <label className="text-sm text-slate-700 dark:text-slate-200">
                  پیوست
                  <span className="text-slate-400 font-normal mr-1">
                    (اختیاری)
                  </span>
                </label>
                <button
                  type="button"
                  onClick={openFilePicker}
                  className="shrink-0 w-8 h-8 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xl leading-none flex items-center justify-center"
                  title="افزودن فایل"
                  aria-label="افزودن فایل"
                >
                  +
                </button>
              </div>

              <button
                type="button"
                onClick={openFilePicker}
                className="w-full flex flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-slate-300 dark:border-slate-600 bg-white dark:bg-[#0D1117] px-4 py-6 hover:border-sky-400 dark:hover:border-sky-500 transition-colors"
              >
                <span className="text-sm text-slate-600 dark:text-slate-200">
                  انتخاب فایل
                </span>
                <span className="text-xs text-slate-400">
                  می‌توانید چند فایل پشت‌سرهم اضافه کنید
                </span>
              </button>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileChange}
              />

              {selectedFiles.length > 0 && (
                <div className="mt-3 space-y-2">
                  {selectedFiles.map((file, index) => (
                    <div
                      key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                      className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-[#0D1117] px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="text-sm text-slate-700 dark:text-slate-200 truncate">
                          {file.name}
                        </p>
                        <p className="text-[11px] text-slate-400">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="shrink-0 text-xs text-red-500 hover:text-red-600"
                      >
                        حذف
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => navigate("/support")}
                className="w-1/2 text-sm bg-slate-600 hover:bg-slate-700 rounded-xl text-white py-2.5"
              >
                انصراف
              </button>
              <button
                type="submit"
                disabled={!isValid || submitting}
                className="w-1/2 text-sm bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-400 rounded-xl text-white py-2.5"
              >
                {submitting ? "در حال ارسال..." : "ارسال تیکت"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
